import type { Locale } from '../i18n/types';
import type { ExerciseResource, PartnerItem, Stage } from '../types';

type ExerciseCopy = Pick<ExerciseResource, 'title' | 'channel' | 'note'>;

/** URL 単位で多言語化（同じ動画が複数ステージで使われる） */
const EXERCISE_BY_URL: Record<string, Partial<Record<Locale, ExerciseCopy>>> = {
  'https://www.youtube.com/watch?v=-3bvlFKeLRE': {
    en: {
      title: 'Gentle breath & stretch (prenatal / postpartum yoga)',
      channel: 'Yoga with Adriene',
      note: 'Breath-led gentle movement. Good for beginners; stop if anything feels wrong.',
    },
    zh: {
      title: '轻柔呼吸与伸展（孕产瑜伽）',
      channel: 'Yoga with Adriene',
      note: '以呼吸带动的轻柔动作，适合初学者。有不适请立即停止。',
    },
  },
  'https://www.youtube.com/watch?v=2_uOYGstJj4': {
    en: {
      title: 'Beginner prenatal / early-postpartum stretch (10 min)',
      channel: 'Pregnancy & postpartum exercise',
      note: 'Short and gentle. Easy to keep up even on tough days.',
    },
    zh: {
      title: '初级孕产伸展（10分钟）',
      channel: '孕产运动频道',
      note: '短时轻柔。身体难受的日子也容易坚持。',
    },
  },
  'https://www.youtube.com/watch?v=zmUJWKM98hM': {
    en: {
      title: 'All-trimester / recovery workout (15 min)',
      channel: 'Pregnancy & postpartum exercise',
      note: 'Full-body session. Check how you feel and go gently.',
    },
    zh: {
      title: '全孕期 / 恢复训练（15分钟）',
      channel: '孕产运动频道',
      note: '全身运动。请根据身体状况温和进行。',
    },
  },
  'https://www.youtube.com/watch?v=Wlu6HsO-pEM': {
    en: {
      title: 'Prenatal full-body workout (25 min)',
      channel: 'Pregnancy & postpartum exercise',
      note: 'A longer full-body session. Rest as needed.',
    },
    zh: {
      title: '孕妇全身训练（25分钟）',
      channel: '孕产运动频道',
      note: '稍长的全身运动。可按体力穿插休息。',
    },
  },
  'https://www.youtube.com/watch?v=feHp2b29G5U': {
    en: {
      title: 'Prenatal / postpartum yoga flow (20 min)',
      channel: 'Pregnancy & postpartum exercise',
      note: 'A flowing yoga session. Keep the range comfortable.',
    },
    zh: {
      title: '孕产瑜伽流程（20分钟）',
      channel: '孕产运动频道',
      note: '连贯的瑜伽练习。请保持舒适活动范围。',
    },
  },
};

function localizeExercises(
  exercises: ExerciseResource[] | undefined,
  locale: Locale
): ExerciseResource[] | undefined {
  if (!exercises?.length) return exercises;
  if (locale === 'ja') return exercises;
  return exercises.map((ex) => {
    const localized = EXERCISE_BY_URL[ex.url]?.[locale];
    if (!localized) return ex;
    return {
      ...ex,
      title: localized.title ?? ex.title,
      channel: localized.channel ?? ex.channel,
      note: localized.note ?? ex.note,
    };
  });
}

function mergePartner(localePartner: PartnerItem[], basePartner: PartnerItem[]): PartnerItem[] {
  const baseById = new Map(basePartner.map((p) => [p.id, p]));
  return localePartner.map((p) => {
    const base = baseById.get(p.id);
    return {
      ...p,
      detail: p.detail ?? base?.detail,
    };
  });
}

/**
 * 日本語ステージを構造の正とし、他言語に欠けているセクション（特に exercises）を補完する。
 */
export function alignStagesWithBase(
  localeStages: Stage[],
  baseStages: Stage[],
  locale: Locale
): Stage[] {
  const baseById = new Map(baseStages.map((s) => [s.id, s]));
  return localeStages.map((stage) => {
    const base = baseById.get(stage.id);
    if (!base) return stage;

    const exercisesSource =
      stage.exercises && stage.exercises.length > 0 ? stage.exercises : base.exercises;
    const careScenes =
      stage.careScenes && stage.careScenes.length > 0 ? stage.careScenes : base.careScenes;

    return {
      ...stage,
      exercises: localizeExercises(exercisesSource, locale),
      careScenes,
      partner: mergePartner(stage.partner ?? [], base.partner ?? []),
      adminGuide:
        stage.adminGuide && stage.adminGuide.length > 0 ? stage.adminGuide : base.adminGuide,
      tips: stage.tips && stage.tips.length > 0 ? stage.tips : base.tips,
      warnings: stage.warnings && stage.warnings.length > 0 ? stage.warnings : base.warnings,
      shopping: stage.shopping && stage.shopping.length > 0 ? stage.shopping : base.shopping,
    };
  });
}
